import {
  ApInvoiceTransactionStatusEnum,
  useUpdateReorderTransactionItem,
} from '@/context/services/transaction_items/List/TransactionItems.service';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

type Props = {
  handlerSuccess: (successMsg: string) => void;
  handlerError: (errorMsg: string) => void;
  showLoading: (loading: boolean) => void;
};

export const useSubmitUpdateReorderTransactionItem = ({
  handlerSuccess,
  handlerError,
  showLoading,
}: Props) => {
  const { report_type_id } = useParams();
  const { mutateAsync } = useUpdateReorderTransactionItem(report_type_id ?? '');
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();

  const onSubmitReorder = useCallback(
    async (orderedIds: string[]) => {
      try {
        showLoading(true);
        const resp = await mutateAsync({
          apReportTypeId: report_type_id ?? '',
          data: {
            ap_invoice_transaction_general_input_ordered_ids: orderedIds,
          },
        });

        if (resp.status === ApInvoiceTransactionStatusEnum.SUCCESS) {
          handlerSuccess(t('transaction_items.update_reorder.success'));
        } else if (
          resp.status === ApInvoiceTransactionStatusEnum.INVALID_ERROR
        ) {
          handlerError(t('transaction_items.update_reorder.error'));
        } else if (resp.status === ApInvoiceTransactionStatusEnum.AUTH_ERROR) {
          location.href = resp.redirectPath;
        } else {
          handlerError(t('transaction_items.update_reorder.error'));
        }
      } catch (error) {
        if (error instanceof Error && error?.name === 'AxiosError') {
          send(error.message);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          send(error as Error);
        }
        handlerError(t('transaction_items.update_reorder.error'));
      } finally {
        showLoading(false);
      }
    },
    [
      showLoading,
      mutateAsync,
      report_type_id,
      handlerSuccess,
      t,
      handlerError,
      send,
    ]
  );

  return { onSubmitReorder };
};
